import React from 'react'

interface PictureProps {
  src1x?: string
  src2x?: string
  mobSrc1x?: string
  mobSrc2x?: string
  deskSrc1x?: string
  deskSrc2x?: string
  mobSvg?: string
  deskSvg?: string
  alt?: string
  lazyLoad?: boolean
  imgClassName?: string
  className?: string
  deskBreakpoint?: string | number
  noBreakpoint?: boolean
  width?: number | string
  height?: number | string
  fetchPriorityHigh?: boolean
  ariaHidden?: boolean
}

const Picture: React.FC<PictureProps> = ({
  src1x,
  src2x,
  mobSrc1x,
  mobSrc2x,
  deskSrc1x,
  deskSrc2x,
  mobSvg,
  deskSvg,
  alt = '',
  lazyLoad = false,
  imgClassName,
  className,
  deskBreakpoint = 768,
  noBreakpoint = false,
  width,
  height,
  fetchPriorityHigh = false,
  ariaHidden = false,
}) => {
  // Check if SVG sources are provided
  const useSvg = Boolean(mobSvg || deskSvg)

  // Determine primary image source
  const imgSrc = useSvg
    ? deskSvg || mobSvg || ''
    : src1x || deskSrc1x || mobSrc1x || ''

  // Generate srcSet for the main img tag when not using SVGs
  // const imgSrcSet = useSvg
  //   ? undefined
  //   : src1x && src2x
  //     ? `${src1x} 1x, ${src2x} 2x`
  //     : undefined

  // Convert breakpoint to a value we can use in media queries
  const breakpointValue =
    typeof deskBreakpoint === 'number'
      ? deskBreakpoint
      : parseInt(deskBreakpoint, 10) || 768

  const mobileBreakpoint = `(max-width: ${breakpointValue - 1}px)`
  const desktopBreakpoint = `(min-width: ${breakpointValue}px)`
  const resolution2x = '(min-resolution: 2dppx)'
  const mobileBreakpoint2dppx = `${resolution2x} and ${mobileBreakpoint}`
  const desktopBreakpoint2dppx = `${resolution2x} and ${desktopBreakpoint}`

  return (
    <picture className={className || undefined}>
      {noBreakpoint ? (
        <source media={resolution2x} srcSet={src2x} />
      ) : (
        <>
          {/* Desktop Source */}
          {deskSvg && <source media={desktopBreakpoint} srcSet={deskSvg} />}
          {src2x && (
            <>
              <source media={resolution2x} srcSet={src2x} />
              <source media={desktopBreakpoint} srcSet={src2x} />
            </>
          )}
          {deskSrc1x && (
            <>
              <source media={desktopBreakpoint2dppx} srcSet={deskSrc2x} />
              <source media={desktopBreakpoint} srcSet={deskSrc1x} />
            </>
          )}

          {/* Mobile Source */}
          {mobSvg && <source media={mobileBreakpoint} srcSet={mobSvg} />}
          {src1x && <source media={mobileBreakpoint} srcSet={src1x} />}
          {mobSrc1x && (
            <>
              <source media={mobileBreakpoint2dppx} srcSet={mobSrc2x} />
              <source media={mobileBreakpoint} srcSet={mobSrc1x} />
            </>
          )}
        </>
      )}

      {/* Fallback Image */}
      <img
        src={imgSrc}
        // srcSet={imgSrcSet}
        alt={alt}
        {...(lazyLoad ? { loading: 'lazy' } : {})}
        {...(imgClassName ? { className: imgClassName } : {})}
        width={width}
        height={height}
        {...(fetchPriorityHigh ? { fetchPriority: 'high' } : {})}
        {...(ariaHidden ? { ariaHidden: 'true' } : {})}
      />
    </picture>
  )
}

export { Picture }
